import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, BarChart3, ChevronDown, Loader2, Mail, Megaphone, Trash2 } from 'lucide-react';
import { PageHeader } from './PageHeader';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import {
  type AdAccountSource,
  type BeatlyApiError,
  type ClientTeamSearchRow,
  type TeamAdAccountRow,
  type TeamPendingAdAccount,
  connectTeamAdAccounts,
  deleteTeamAdAccount,
  getTeamAdAccountsPendingSession,
  getTeamMetaAuthorizeUrl,
  getTeamTiktokAuthorizeUrl,
  getTeamsSearch,
  listTeamAdAccounts,
  requestTeamAdAccountBackfill,
  selectClientTeam,
} from '@/api/beatlyApi';
import { getTokenFromStorage, updateAuthInStorage } from '@/utils/auth';
import { showError } from '@/utils/alert';
import { sortClientTeamsByName } from '@/utils/sortClientTeamsByName';
import { salesContactLink } from '@/utils/salesContact';

function isBeatlyApiError(err: unknown): err is BeatlyApiError {
  return typeof err === 'object' && err !== null && 'message' in err;
}

/** Maps api-v2 ad-accounts error codes to user-facing i18n messages. */
function mapAdAccountsError(err: unknown, t: (key: string) => string): string {
  if (isBeatlyApiError(err)) {
    if (err.code === 'AD_ACCOUNTS_TEAM_ADMIN_REQUIRED') {
      return t('adAccountsPage.errors.adminRequired');
    }
    if (err.code === 'AD_ACCOUNTS_NOT_ENABLED') {
      return t('adAccountsPage.errors.notEnabled');
    }
    if (err.message) return err.message;
  }
  return t('adAccountsPage.errors.generic');
}

interface AdAccountsUpsellPanelProps {
  salesHref: string;
  salesOpensInNewTab: boolean;
}

/** Sales pitch when ad accounts are not enabled for the selected team (mention-monitoring CTA pattern). */
function AdAccountsUpsellPanel({ salesHref, salesOpensInNewTab }: AdAccountsUpsellPanelProps) {
  const { t } = useTranslation();

  return (
    <div className="px-6 py-8">
      <div
        className="rounded-2xl border border-[#be43ff]/20 bg-gradient-to-br from-white to-[#faf5ff] p-6 shadow-sm"
        role="region"
        aria-label={t('adAccountsPage.upsell.regionAria')}
        data-testid="ad-accounts-upsell"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#a9f600] shrink-0" aria-hidden />
            <p className="text-sm font-bold leading-snug text-[#101828]">
              {t('adAccountsPage.upsell.headline')}
            </p>
          </div>
          <p className="text-sm text-[#667085] leading-relaxed">
            {t('adAccountsPage.upsell.intro')}
          </p>
          <ol className="space-y-2 text-xs text-[#344054]">
            <li className="flex gap-2">
              <span
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-[#a9f600]/25 text-[#a9f600] font-bold"
                aria-hidden
              >
                1
              </span>
              <span>{t('adAccountsPage.upsell.benefit1')}</span>
            </li>
            <li className="flex gap-2">
              <span
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-[#be43ff]/15 text-[#be43ff] font-bold"
                aria-hidden
              >
                2
              </span>
              <span>{t('adAccountsPage.upsell.benefit2')}</span>
            </li>
          </ol>
          <a
            href={salesHref}
            target={salesOpensInNewTab ? '_blank' : undefined}
            rel={salesOpensInNewTab ? 'noopener noreferrer' : undefined}
            aria-label={t('adAccountsPage.upsell.contactSalesAria')}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl h-10 px-4 text-xs text-[#be43ff] hover:bg-[#be43ff]/5 transition-colors"
            style={{ fontWeight: 600 }}
          >
            {t('adAccountsPage.upsell.contactSales')}
            <ArrowUpRight className="w-3.5 h-3.5" aria-hidden />
          </a>
        </div>
      </div>
    </div>
  );
}

interface AdAccountsDisabledConnectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountManagerName: string;
  mailHref?: string;
}

/** Explains that connect is off for the team; email CTA matches dashboard account manager widget. */
function AdAccountsDisabledConnectDialog({
  open,
  onOpenChange,
  accountManagerName,
  mailHref,
}: AdAccountsDisabledConnectDialogProps) {
  const { t } = useTranslation();
  const unavailable = t('dashboard.accountManager.contactUnavailable');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[480px] rounded-2xl border border-gray-100 bg-white p-0 shadow-[0_16px_48px_rgba(16,24,40,0.16)]"
        data-testid="ad-accounts-disabled-connect-modal"
      >
        <DialogHeader className="px-6 pt-6 pb-3">
          <DialogTitle className="text-[#101828] text-[18px]" style={{ fontWeight: 700 }}>
            {t('adAccountsPage.disabledConnectModal.title')}
          </DialogTitle>
          <DialogDescription className="text-[#667085] text-sm mt-1 leading-relaxed" style={{ fontWeight: 500 }}>
            {t('adAccountsPage.disabledConnectModal.body')}
          </DialogDescription>
        </DialogHeader>
        <div className="px-6 pb-6 space-y-4">
          <p className="text-sm text-[#344054]">
            <span className="text-[#667085]">{t('dashboard.accountManager.roleLabel')}: </span>
            <span style={{ fontWeight: 600 }}>{accountManagerName}</span>
          </p>
          {mailHref ? (
            <a
              href={mailHref}
              className="inline-flex items-center justify-center gap-2 w-full rounded-xl h-10 px-4 text-sm text-[#be43ff] border border-[#be43ff]/30 hover:bg-[#be43ff]/5 transition-colors"
              style={{ fontWeight: 600 }}
              aria-label={t('dashboard.accountManager.contactEmail')}
            >
              <Mail className="w-4 h-4 shrink-0" aria-hidden />
              {t('dashboard.accountManager.contactEmail')}
            </a>
          ) : (
            <p className="text-sm text-[#98a2b3] text-center py-2" role="status">
              {unavailable}
            </p>
          )}
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="w-full rounded-xl h-10 px-4 text-sm text-[#344054] border border-gray-200 hover:bg-gray-50 transition-colors"
            style={{ fontWeight: 600 }}
          >
            {t('adAccountsPage.disabledConnectModal.close')}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Team-scoped Meta/TikTok ad account management.
 * OAuth return lands on `/ad-accounts/connect?session=…`; admins connect accounts, others see read-only list.
 * When `has_ad_accounts_enabled` is false, shows upsell and opens a modal on connect click.
 */
export const AdAccountsPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionFromUrl = searchParams.get('session');
  const { user, setUser, logout } = useAuth();

  const currentUserTeamId = typeof user?.team?.team_id === 'string' ? user.team.team_id : null;

  const [teams, setTeams] = useState<ClientTeamSearchRow[]>([]);
  const [currentTeamId, setCurrentTeamId] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<TeamAdAccountRow[]>([]);
  const [isLoadingTeams, setIsLoadingTeams] = useState(true);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [pendingActionId, setPendingActionId] = useState<string | null>(null);
  const [isTeamPickerOpen, setIsTeamPickerOpen] = useState(false);
  const [isSwitchingTeam, setIsSwitchingTeam] = useState(false);
  const [isDisabledConnectModalOpen, setIsDisabledConnectModalOpen] = useState(false);

  const [connectPicker, setConnectPicker] = useState<{
    sessionId: string;
    source: AdAccountSource;
    accounts: TeamPendingAdAccount[];
  } | null>(null);
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [backfillTarget, setBackfillTarget] = useState<TeamAdAccountRow | null>(null);
  const [backfillMonths, setBackfillMonths] = useState(6);
  const [backfillNotice, setBackfillNotice] = useState<string | null>(null);

  const teamsSorted = useMemo(() => sortClientTeamsByName(teams), [teams]);

  const currentTeam = useMemo(
    () => teams.find((row) => row.team_id === currentTeamId) ?? null,
    [teams, currentTeamId],
  );

  const hasAdAccountsEnabled = currentTeam?.has_ad_accounts_enabled === true;
  const isAdmin = currentTeam?.role === 'ADMIN';
  const connectBusy = isConnecting || !currentTeamId;

  const accountManager = user?.team?.account_manager;
  const mailHref = accountManager?.email ? `mailto:${accountManager.email}` : undefined;
  const accountManagerName = accountManager?.name ?? t('dashboard.accountManager.fallbackName');

  const { href: salesHref, external: salesOpensInNewTab } = salesContactLink(accountManager?.email);

  const currentTeamName = useMemo(() => {
    if (currentTeam?.name) return currentTeam.name;
    if (typeof user?.team?.name === 'string' && user.team.name) return user.team.name;
    return t('adAccountsPage.teamFallback');
  }, [currentTeam?.name, user?.team?.name, t]);

  const loadAccounts = useCallback(async (teamId: string) => {
    const token = getTokenFromStorage();
    if (!token) {
      logout?.();
      return;
    }
    setIsLoadingAccounts(true);
    try {
      const res = await listTeamAdAccounts(teamId, token);
      setAccounts(res.accounts ?? []);
    } catch (err) {
      showError(mapAdAccountsError(err, t));
      setAccounts([]);
    } finally {
      setIsLoadingAccounts(false);
    }
  }, [logout, t]);

  // Load teams and pick initial selection: prefer JWT team, else first team
  useEffect(() => {
    let cancelled = false;
    async function run() {
      const token = getTokenFromStorage();
      if (!token) {
        logout?.();
        return;
      }
      setIsLoadingTeams(true);
      try {
        const res = await getTeamsSearch(token);
        if (cancelled) return;
        const rows = Array.isArray(res?.teams) ? res.teams : [];
        setTeams(rows);
        const preferred =
          rows.find((row) => row.team_id === currentUserTeamId)?.team_id
          ?? rows[0]?.team_id
          ?? null;
        setCurrentTeamId(preferred);
      } catch {
        if (!cancelled) setCurrentTeamId(currentUserTeamId);
      } finally {
        if (!cancelled) setIsLoadingTeams(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [currentUserTeamId, logout]);

  // When ad accounts are disabled for the team, skip list fetch and show upsell in the table body
  useEffect(() => {
    if (!currentTeamId) return;
    if (!hasAdAccountsEnabled) {
      setAccounts([]);
      setIsLoadingAccounts(false);
      return;
    }
    void loadAccounts(currentTeamId);
  }, [currentTeamId, hasAdAccountsEnabled, loadAccounts]);

  // OAuth callback: resolve pending session only when ad accounts are enabled for the team
  useEffect(() => {
    if (!sessionFromUrl || !currentTeamId || !hasAdAccountsEnabled) return;
    let cancelled = false;
    async function loadPending() {
      const token = getTokenFromStorage();
      if (!token) {
        logout?.();
        return;
      }
      setIsConnecting(true);
      try {
        const pending = await getTeamAdAccountsPendingSession(currentTeamId!, sessionFromUrl!, token);
        if (cancelled) return;
        const ids = (pending.accounts ?? []).map((a) => a.account_id);
        setConnectPicker({
          sessionId: pending.session_id,
          source: pending.source,
          accounts: pending.accounts ?? [],
        });
        setSelectedAccountIds(ids);
      } catch (err) {
        if (!cancelled) {
          showError(mapAdAccountsError(err, t));
          setSearchParams({});
        }
      } finally {
        if (!cancelled) setIsConnecting(false);
      }
    }
    void loadPending();
    return () => {
      cancelled = true;
    };
  }, [sessionFromUrl, currentTeamId, hasAdAccountsEnabled, logout, setSearchParams, t]);

  const handleSelectTeam = async (teamId: string) => {
    if (!teamId || teamId === currentTeamId) {
      setIsTeamPickerOpen(false);
      return;
    }
    const token = getTokenFromStorage();
    if (!token) {
      logout?.();
      return;
    }
    if (isSwitchingTeam) return;
    setIsSwitchingTeam(true);
    try {
      const { token: newToken, user: updatedUser } = await selectClientTeam(teamId, token);
      updateAuthInStorage({ token: newToken, user: updatedUser });
      setUser?.(updatedUser as never);
      setCurrentTeamId(teamId);
      setIsTeamPickerOpen(false);
      setConnectPicker(null);
      setSearchParams({});
    } catch (err) {
      showError(mapAdAccountsError(err, t));
    } finally {
      setIsSwitchingTeam(false);
    }
  };

  const handleConnectClick = (source: AdAccountSource) => {
    if (!hasAdAccountsEnabled) {
      setIsDisabledConnectModalOpen(true);
      return;
    }
    void handleConnectPlatform(source);
  };

  const handleConnectPlatform = async (source: AdAccountSource) => {
    if (!currentTeamId || !isAdmin || !hasAdAccountsEnabled) return;
    const token = getTokenFromStorage();
    if (!token) {
      logout?.();
      return;
    }
    setIsConnecting(true);
    try {
      const res = source === 'meta'
        ? await getTeamMetaAuthorizeUrl(currentTeamId, token)
        : await getTeamTiktokAuthorizeUrl(currentTeamId, token);
      window.location.href = res.url;
    } catch (err) {
      showError(mapAdAccountsError(err, t));
      setIsConnecting(false);
    }
  };

  const clearConnectSession = () => {
    setConnectPicker(null);
    setSelectedAccountIds([]);
    setSearchParams({});
  };

  const handleFinalizeConnect = async () => {
    if (!connectPicker || !currentTeamId || selectedAccountIds.length === 0) return;
    const token = getTokenFromStorage();
    if (!token) {
      logout?.();
      return;
    }
    setIsConnecting(true);
    try {
      await connectTeamAdAccounts(currentTeamId, token, {
        session_id: connectPicker.sessionId,
        account_ids: selectedAccountIds,
      });
      clearConnectSession();
      await loadAccounts(currentTeamId);
    } catch (err) {
      showError(mapAdAccountsError(err, t));
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async (row: TeamAdAccountRow) => {
    if (!currentTeamId || !isAdmin) return;
    const token = getTokenFromStorage();
    if (!token) {
      logout?.();
      return;
    }
    const actionId = `${row.source}:${row.account_id}`;
    setPendingActionId(actionId);
    try {
      await deleteTeamAdAccount(currentTeamId, row.source, row.account_id, token);
      await loadAccounts(currentTeamId);
    } catch (err) {
      showError(mapAdAccountsError(err, t));
    } finally {
      setPendingActionId(null);
    }
  };

  const togglePickerSelection = (accountId: string) => {
    setSelectedAccountIds((prev) => (
      prev.includes(accountId)
        ? prev.filter((id) => id !== accountId)
        : [...prev, accountId]
    ));
  };

  const platformLabel = (source: AdAccountSource) => (
    source === 'meta' ? t('adAccountsPage.platform.meta') : t('adAccountsPage.platform.tiktok')
  );

  const backfillStatusLabel = (status?: string | null) => {
    const key = (status || '').toLowerCase();
    if (key === 'pending' || key === 'running' || key === 'done' || key === 'error') {
      return t(`adAccountsPage.backfill.status.${key}`);
    }
    return t('adAccountsPage.backfill.status.idle');
  };

  const openBackfillModal = (row: TeamAdAccountRow) => {
    const max = row.backfill_max_months || (row.source === 'tiktok' ? 30 : 37);
    setBackfillMonths(Math.min(6, max));
    setBackfillTarget(row);
  };

  const handleConfirmBackfill = async () => {
    if (!backfillTarget || !currentTeamId || !isAdmin) return;
    const token = getTokenFromStorage();
    if (!token) {
      logout();
      return;
    }
    const max = backfillTarget.backfill_max_months || (backfillTarget.source === 'tiktok' ? 30 : 37);
    const months = Math.max(1, Math.min(max, Math.floor(backfillMonths) || 1));
    const inFlight = ['pending', 'running'].includes((backfillTarget.backfill_status || '').toLowerCase());
    const actionId = `backfill:${backfillTarget.source}:${backfillTarget.account_id}`;
    setPendingActionId(actionId);
    try {
      const updated = await requestTeamAdAccountBackfill(
        currentTeamId,
        backfillTarget.source,
        backfillTarget.account_id,
        { months, force: inFlight },
        token,
      );
      setAccounts((prev) => prev.map((a) => (
        a.source === updated.source && a.account_id === updated.account_id ? updated : a
      )));
      setBackfillTarget(null);
      setBackfillNotice(t('adAccountsPage.backfill.queued'));
    } catch (err) {
      showError(mapAdAccountsError(err, t) || t('adAccountsPage.errors.backfillFailed'));
    } finally {
      setPendingActionId(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <PageHeader
        title={t('adAccountsPage.title')}
        subtitle={t('adAccountsPage.subtitle')}
      />

      <div className="mb-8 relative inline-block">
        <button
          type="button"
          onClick={() => setIsTeamPickerOpen((open) => !open)}
          disabled={isLoadingTeams || teamsSorted.length === 0}
          aria-label={t('adAccountsPage.teamPickerAria', { name: currentTeamName })}
          aria-expanded={isTeamPickerOpen}
          aria-haspopup="listbox"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 bg-white text-sm text-[#344054] hover:bg-gray-50 disabled:opacity-60"
          style={{ fontWeight: 600 }}
        >
          {isSwitchingTeam ? (
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
          ) : (
            <Megaphone className="w-4 h-4 text-[#be43ff]" aria-hidden />
          )}
          {currentTeamName}
          <ChevronDown className="w-4 h-4 text-gray-400" aria-hidden />
        </button>
        {isTeamPickerOpen && teamsSorted.length > 0 ? (
          <div
            className="absolute left-0 mt-2 min-w-[220px] bg-white rounded-xl border border-gray-100 py-1 z-20"
            role="listbox"
            aria-label={t('adAccountsPage.teamPickerAria', { name: currentTeamName })}
            style={{ boxShadow: '0 8px 30px rgba(0,0,0,0.12)' }}
          >
            {teamsSorted.map((team) => (
              <button
                key={team.team_id}
                type="button"
                role="option"
                aria-selected={team.team_id === currentTeamId}
                onClick={() => void handleSelectTeam(team.team_id)}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                  team.team_id === currentTeamId ? 'text-[#2e024c] font-semibold' : 'text-gray-700'
                }`}
              >
                {team.name}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {!isAdmin ? (
        <div
          className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          role="status"
        >
          {t('adAccountsPage.readOnlyBanner')}
        </div>
      ) : null}

      {isAdmin ? (
        <div className="flex flex-wrap gap-3 mb-8">
          <button
            type="button"
            disabled={connectBusy}
            onClick={() => handleConnectClick('meta')}
            className="inline-flex items-center justify-center px-4 py-2 rounded-xl border border-gray-200 bg-white text-sm text-[#344054] hover:bg-gray-50 disabled:opacity-60"
            style={{ fontWeight: 600 }}
          >
            {t('adAccountsPage.connectMeta')}
          </button>
          <button
            type="button"
            disabled={connectBusy}
            onClick={() => handleConnectClick('tiktok')}
            className="inline-flex items-center justify-center px-4 py-2 rounded-xl border border-gray-200 bg-white text-sm text-[#344054] hover:bg-gray-50 disabled:opacity-60"
            style={{ fontWeight: 600 }}
          >
            {t('adAccountsPage.connectTiktok')}
          </button>
        </div>
      ) : null}

      <AdAccountsDisabledConnectDialog
        open={isDisabledConnectModalOpen}
        onOpenChange={setIsDisabledConnectModalOpen}
        accountManagerName={accountManagerName}
        mailHref={mailHref}
      />

      {connectPicker ? (
        <div className="mb-8 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
          <h3 className="text-lg text-[#101828] mb-1" style={{ fontWeight: 600 }}>
            {t('adAccountsPage.picker.title')}
          </h3>
          <p className="text-sm text-[#667085] mb-4">
            {t('adAccountsPage.picker.description', { platform: platformLabel(connectPicker.source) })}
          </p>
          <div className="space-y-2 mb-4">
            {connectPicker.accounts.map((account) => (
              <label
                key={account.account_id}
                className="flex items-start gap-3 p-3 rounded-xl border border-gray-100 cursor-pointer hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  checked={selectedAccountIds.includes(account.account_id)}
                  onChange={() => togglePickerSelection(account.account_id)}
                  className="mt-1"
                />
                <span>
                  <span className="block text-sm text-[#101828]" style={{ fontWeight: 600 }}>
                    {account.account_name || account.account_id}
                  </span>
                  <span className="block text-xs text-[#667085]">{account.account_id}</span>
                </span>
              </label>
            ))}
          </div>
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={clearConnectSession}
              className="px-4 py-2 rounded-xl border border-gray-200 text-sm text-[#344054] hover:bg-gray-50"
              style={{ fontWeight: 600 }}
            >
              {t('adAccountsPage.picker.cancel')}
            </button>
            <button
              type="button"
              disabled={isConnecting || selectedAccountIds.length === 0}
              onClick={() => void handleFinalizeConnect()}
              className="px-4 py-2 rounded-xl bg-[#2e024c] text-white text-sm hover:bg-[#3a0d5e] disabled:opacity-60"
              style={{ fontWeight: 700 }}
            >
              {t('adAccountsPage.picker.connectSelected')}
            </button>
          </div>
        </div>
      ) : null}

      <div className="rounded-2xl border border-gray-100 bg-white overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-base text-[#101828]" style={{ fontWeight: 600 }}>
            {t('adAccountsPage.tableTitle')}
          </h2>
        </div>
        {isLoadingAccounts ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[#be43ff]" aria-hidden />
          </div>
        ) : !hasAdAccountsEnabled ? (
          <AdAccountsUpsellPanel
            salesHref={salesHref}
            salesOpensInNewTab={salesOpensInNewTab}
          />
        ) : accounts.length === 0 ? (
          <p className="px-6 py-10 text-sm text-[#667085] text-center">{t('adAccountsPage.empty')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[#667085] border-b border-gray-100">
                  <th className="px-6 py-3 font-medium">{t('adAccountsPage.table.platform')}</th>
                  <th className="px-6 py-3 font-medium">{t('adAccountsPage.table.name')}</th>
                  <th className="px-6 py-3 font-medium">{t('adAccountsPage.table.accountId')}</th>
                  <th className="px-6 py-3 font-medium">{t('adAccountsPage.table.connectedBy')}</th>
                  <th className="px-6 py-3 font-medium">{t('adAccountsPage.table.backfill')}</th>
                  {isAdmin ? (
                    <th className="px-6 py-3 font-medium">{t('adAccountsPage.table.actions')}</th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {accounts.map((row) => {
                  const actionId = `${row.source}:${row.account_id}`;
                  const backfillBusy = pendingActionId === `backfill:${actionId}`;
                  return (
                    <tr key={actionId} className="border-b border-gray-50 last:border-0">
                      <td className="px-6 py-4 text-[#101828]">{platformLabel(row.source)}</td>
                      <td className="px-6 py-4 text-[#101828]">{row.account_name || t('common.notAvailable')}</td>
                      <td className="px-6 py-4 text-[#667085] font-mono text-xs">{row.account_id}</td>
                      <td className="px-6 py-4 text-[#667085]">{row.connected_by_email || t('common.notAvailable')}</td>
                      <td className="px-6 py-4 text-[#667085]" title={row.backfill_error || row.backfill_since || undefined}>
                        {backfillStatusLabel(row.backfill_status)}
                      </td>
                      {isAdmin ? (
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => openBackfillModal(row)}
                              disabled={backfillBusy}
                              className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-[#344054] hover:bg-gray-50 disabled:opacity-50"
                              style={{ fontWeight: 600 }}
                            >
                              {backfillBusy ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin inline" aria-hidden />
                              ) : (
                                t('adAccountsPage.actions.backfill')
                              )}
                            </button>
                            <button
                              type="button"
                              aria-label={t('adAccountsPage.actions.disconnect')}
                              disabled={pendingActionId === actionId}
                              onClick={() => void handleDisconnect(row)}
                              className="p-2 rounded-lg text-[#667085] hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                            >
                              {pendingActionId === actionId ? (
                                <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                              ) : (
                                <Trash2 className="w-4 h-4" aria-hidden />
                              )}
                            </button>
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!backfillTarget} onOpenChange={(open) => { if (!open) setBackfillTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('adAccountsPage.backfill.modalTitle')}</DialogTitle>
            <DialogDescription>
              {backfillTarget
                ? t('adAccountsPage.backfill.modalDescription', {
                  name: backfillTarget.account_name || backfillTarget.account_id,
                  max: backfillTarget.backfill_max_months || (backfillTarget.source === 'tiktok' ? 30 : 37),
                  platform: platformLabel(backfillTarget.source),
                })
                : null}
            </DialogDescription>
          </DialogHeader>
          {backfillTarget ? (
            <div className="space-y-4">
              <label className="block text-sm text-[#344054]">
                <span className="block mb-1" style={{ fontWeight: 600 }}>
                  {t('adAccountsPage.backfill.monthsLabel')}
                </span>
                <input
                  type="number"
                  min={1}
                  max={backfillTarget.backfill_max_months || (backfillTarget.source === 'tiktok' ? 30 : 37)}
                  value={backfillMonths}
                  onChange={(e) => setBackfillMonths(Number(e.target.value))}
                  className="w-24 px-3 py-2 rounded-lg border border-gray-200 text-sm"
                />
                <span className="block mt-1 text-xs text-[#667085]">
                  {t('adAccountsPage.backfill.monthsHint', {
                    max: backfillTarget.backfill_max_months || (backfillTarget.source === 'tiktok' ? 30 : 37),
                  })}
                </span>
              </label>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setBackfillTarget(null)}
                  className="px-4 py-2 rounded-xl border border-gray-200 text-sm text-[#344054] hover:bg-gray-50"
                  style={{ fontWeight: 600 }}
                >
                  {t('adAccountsPage.backfill.cancel')}
                </button>
                <button
                  type="button"
                  onClick={() => void handleConfirmBackfill()}
                  disabled={pendingActionId?.startsWith('backfill:') || false}
                  className="px-4 py-2 rounded-xl bg-[#2e024c] text-white text-sm hover:bg-[#3a0d5e] disabled:opacity-60"
                  style={{ fontWeight: 700 }}
                >
                  {t('adAccountsPage.backfill.confirm')}
                </button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {backfillNotice ? (
        <div
          className="fixed bottom-6 right-6 z-40 max-w-sm rounded-xl border border-gray-100 bg-white px-4 py-3 text-sm text-[#344054] shadow-lg"
          role="status"
        >
          <div className="flex items-start gap-3">
            <span className="flex-1">{backfillNotice}</span>
            <button
              type="button"
              onClick={() => setBackfillNotice(null)}
              className="text-[#667085] hover:text-[#101828]"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
